import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import { ResetApiState, ResponseWrapper } from "../../redux.type";
import { queryStringGenerator } from "../../redux.util";
import {
  AgentDailyAudit,
  AgentDailySummary,
  AgentDailyTransactionAudit,
  AgentDateTripAudit,
  AgentDurationSummary,
  AgentDurationTicketSummary,
  AgentFinanceAudit,
  ConductorDailyAudit,
  ConductorDailySummary,
  ConductorDailyTransactionAudit,
  ConductorDurationSummary,
  ConductorDurationTicketSummary,
  ConductorFinanceAudit,
  ConductorTripAudit,
  FinanceAuditStateTypes,
  OtherDurationTicketSummary,
  TripPaymentStatus,
} from "./FinanceAudit.util";

const name = "finance-audit";
const extraActions = createExtraActions();

const ConductorFinanceAuditInitialState: FinanceAuditStateTypes = {
  createConductorFinanceAudit: ResetApiState(null),
  resetCreateConductorFinanceAudit: ResetApiState(null),
  editConductorFinanceAudit: ResetApiState(null),
  resetEditConductorFinanceAudit: ResetApiState(null),
  deleteConductorFinanceAudit: ResetApiState(null),
  resetDeleteConductorFinanceAudit: ResetApiState(null),
  conductorFinanceAudits: ResetApiState([]),
  resetConductorFinanceAudits: ResetApiState(null),

  createAgentFinanceAudit: ResetApiState(null),
  resetCreateAgentFinanceAudit: ResetApiState(null),
  agentFinanceAudits: ResetApiState([]),
  resetAgentFinanceAudits: ResetApiState(null),

  conductorDailyAudits: ResetApiState([]),
  conductorDailyAuditTransactions: ResetApiState([]),
  conductorTripAudits: ResetApiState([]),
  conductorDurationSummary: ResetApiState([]),
  conductorDurationTicketSummary: ResetApiState([]),
  conductorDailySummary: ResetApiState([]),

  agentDailyAudits: ResetApiState([]),
  agentDailyAuditTransactions: ResetApiState([]),
  agentTripAudits: ResetApiState([]),
  agentDurationSummary: ResetApiState([]),
  agentDurationTicketSummary: ResetApiState([]),
  agentDailySummary: ResetApiState([]),

  otherDurationTicketSummary: ResetApiState([]),

  tripsPaymentStatuses: ResetApiState([]),

  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const createConductorFinanceAudit = createAsyncThunk<
    ResponseWrapper<ConductorFinanceAudit>,
    any
  >(`/create-conductor-${name}`, async (data: any, thunkApi) =>
    axios
      .post(`/${name}/conductor`, data)
      .then((response) => {
        if (response.data.success) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCreateConductorFinanceAudit = createAction(
    `/reset-create-conductor-${name}`
  );

  const editConductorFinanceAudit = createAsyncThunk<
    ResponseWrapper<ConductorFinanceAudit>,
    any
  >(`/edit-${name}`, async ({ data, id }: any, thunkApi) =>
    axios
      .put(`/${name}/conductor/${id}`, data)
      .then((response) => {
        if (response.data.success) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetEditConductorFinanceAudit = createAction(`/reset-edit-${name}`);

  const deleteConductorFinanceAudit = createAsyncThunk<number, any>(
    `/delete-${name}`,
    async (id: any, thunkApi) =>
      axios
        .delete(`/${name}/conductor/${id}`)
        .then((response) => {
          if (response.data.success) {
            return id;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error?.response?.data);
        })
  );

  const resetDeleteConductorFinanceAudit = createAction(
    `/reset-delete-${name}`
  );

  const getConductorFinanceAudits = createAsyncThunk<
    ConductorFinanceAudit[],
    any
  >(`/get-conductor-${name}s`, async (query: any, thunkApi) => {
    return axios
      .get(`/${name}/conductor/${queryStringGenerator(query)}`)
      .then((response) => {
        if (response.data) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error: AxiosError) => {
        return thunkApi.rejectWithValue(error.message);
      });
  });
  const resetGetConductorFinanceAudits = createAction(
    `/reset-get-conductor-${name}s`
  );

  const createAgentFinanceAudit = createAsyncThunk<
    ResponseWrapper<AgentFinanceAudit>,
    any
  >(`/create-agent-${name}`, async (data: any, thunkApi) =>
    axios
      .post(`/${name}/agent`, data)
      .then((response) => {
        if (response.data.success) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCreateAgentFinanceAudit = createAction(
    `/reset-create-agent-${name}`
  );

  const getAgentFinanceAudits = createAsyncThunk<AgentFinanceAudit[], any>(
    `/get-agent-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}/agent/${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );
  const resetGetAgentFinanceAudits = createAction(`/reset-get-agent-${name}s`);

  const getConductorDailyAudits = createAsyncThunk<ConductorDailyAudit[], any>(
    `/get-${name}-conductor-s`,
    async (date: any, thunkApi) => {
      return axios
        .get(`/${name}/conductors/date/${date}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getConductorDailyAuditsByDateRange = createAsyncThunk<
    ConductorDailyAudit[],
    { startDate: string; endDate: string }
  >(
    `/get-${name}-conductor-s-date-range`,
    async ({ startDate, endDate }, thunkApi) => {
      return axios
        .get(
          `/${name}/conductors/date/placeholder?startDate=${startDate}&endDate=${endDate}`
        )
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getConductorDailyTransactionAudits = createAsyncThunk<
    ConductorDailyTransactionAudit[],
    any
  >(`/get-${name}-conductor-transactions`, async (date: any, thunkApi) => {
    return axios
      .get(`/${name}/conductors/date/${date}/transactions`)
      .then((response) => {
        if (response.data) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error: AxiosError) => {
        return thunkApi.rejectWithValue(error.message);
      });
  });

  const getConductorTripAudits = createAsyncThunk<ConductorTripAudit[], any>(
    `/get-${name}-conductor-trips`,
    async (
      payload: {
        id: any;
        date: {
          startDate: string;
          endDate: string;
        };
      },
      thunkApi
    ) => {
      const query = queryStringGenerator(payload.date);
      return axios
        .get(`/${name}/trips/conductor/${payload.id}${query}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getConductorDurationSummary = createAsyncThunk<
    ConductorDurationSummary[],
    any
  >(
    `/get-${name}-conductor-duration-summary`,
    async ({ start_date, end_date }: any, thunkApi) => {
      return axios
        .get(`/${name}/conductor-duration-summary/${start_date}/${end_date}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getConductorDurationTicketSummary = createAsyncThunk<
    ConductorDurationTicketSummary[],
    any
  >(
    `/get-${name}-conductor-duration-ticket-summary`,
    async ({ start_date, end_date }: any, thunkApi) => {
      return axios
        .get(
          `/${name}/conductor-duration-ticket-summary/${start_date}/${end_date}`
        )
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getConductorDailySummary = createAsyncThunk<
    ConductorDailySummary[],
    any
  >(
    `/get-${name}-conductor-duration-detailed-summary`,
    async ({ conductorId, start_date, end_date }: any, thunkApi) => {
      return axios
        .get(
          `/${name}/conductor-duration-detailed-summary/${conductorId}/${start_date}/${end_date}`
        )
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getAgentDailyAudits = createAsyncThunk<AgentDailyAudit[], any>(
    `/get-${name}-agents`,
    async (
      params: string | { date?: string; startDate?: string; endDate?: string },
      thunkApi
    ) => {
      // Handle both old format (string) and new format (object)
      let url: string;

      if (typeof params === "string") {
        // Old format: just a date string
        url = `/${name}/agents/date/${params}`;
      } else {
        // New format: object with date or startDate/endDate
        const date = params.date || params.startDate || "";
        url = `/${name}/agents/date/${date}`;

        // If startDate and endDate are provided, add them as query parameters
        if (params.startDate && params.endDate) {
          url += `?startDate=${params.startDate}&endDate=${params.endDate}`;
        }
      }

      return axios
        .get(url)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getAgentDailyTransactionAudits = createAsyncThunk<
    AgentDailyTransactionAudit[],
    any
  >(`/get-${name}-agent-transactions`, async (date: any, thunkApi) => {
    return axios
      .get(`/${name}/agents/date/${date}/transactions`)
      .then((response) => {
        if (response.data) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error: AxiosError) => {
        return thunkApi.rejectWithValue(error.message);
      });
  });

  const getAgentTripAudits = createAsyncThunk<AgentDateTripAudit[], any>(
    `/get-${name}-agent-trips`,
    async (id: any, thunkApi) => {
      return axios
        .get(`/${name}/trips/agent/${id}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getAgentDurationSummary = createAsyncThunk<AgentDurationSummary[], any>(
    `/get-${name}-agent-duration-summary`,
    async ({ start_date, end_date }: any, thunkApi) => {
      return axios
        .get(`/${name}/agent-duration-summary/${start_date}/${end_date}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getAgentDurationTicketSummary = createAsyncThunk<
    AgentDurationTicketSummary[],
    any
  >(
    `/get-${name}-agent-duration-ticket-summary`,
    async ({ start_date, end_date }: any, thunkApi) => {
      return axios
        .get(`/${name}/agent-duration-ticket-summary/${start_date}/${end_date}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getOtherDurationTicketSummary = createAsyncThunk<
    OtherDurationTicketSummary[],
    any
  >(
    `/get-${name}-other-duration-ticket-summary`,
    async ({ start_date, end_date }: any, thunkApi) => {
      return axios
        .get(`/${name}/other-duration-ticket-summary/${start_date}/${end_date}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getAgentDailySummary = createAsyncThunk<AgentDailySummary[], any>(
    `/get-${name}-agent-duration-detailed-summary`,
    async ({ agentId, start_date, end_date }: any, thunkApi) => {
      return axios
        .get(
          `/${name}/agent-duration-detailed-summary/${agentId}/${start_date}/${end_date}`
        )
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getTripsPaymentStatus = createAsyncThunk<TripPaymentStatus[], any>(
    `/get-${name}-trip-payment-general-summary`,
    async ({ start_date, end_date }: any, thunkApi) => {
      return axios
        .get(`/${name}/trip-payment-general-summary/${start_date}/${end_date}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const resetStates = createAction(`/reset-states`);

  return {
    createConductorFinanceAudit,
    resetCreateConductorFinanceAudit,
    editConductorFinanceAudit,
    resetEditConductorFinanceAudit,
    deleteConductorFinanceAudit,
    resetDeleteConductorFinanceAudit,
    getConductorFinanceAudits,
    resetGetConductorFinanceAudits,

    createAgentFinanceAudit,
    resetCreateAgentFinanceAudit,
    getAgentFinanceAudits,
    resetGetAgentFinanceAudits,

    getConductorDailyAudits,
    getConductorDailyAuditsByDateRange,
    getConductorDailyTransactionAudits,
    getConductorTripAudits,
    getConductorDurationSummary,
    getConductorDurationTicketSummary,
    getConductorDailySummary,

    getAgentDailyAudits,
    getAgentDailyTransactionAudits,
    getAgentTripAudits,
    getAgentDurationSummary,
    getAgentDurationTicketSummary,
    getAgentDailySummary,

    getOtherDurationTicketSummary,

    getTripsPaymentStatus,

    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<FinanceAuditStateTypes>
) {
  return {
    ...createConductorFinanceAuditReducer(),
    ...resetCreateConductorFinanceAudit(),
    ...editConductorFinanceAuditReducer(),
    ...resetEditConductorFinanceAudit(),
    ...deleteConductorFinanceAuditReducer(),
    ...resetDeleteConductorFinanceAudit(),
    ...conductorFinanceAuditsReducer(),
    ...resetGetConductorFinanceAudits(),

    ...createAgentFinanceAuditReducer(),
    ...resetCreateAgentFinanceAudit(),
    ...agentFinanceAuditsReducer(),
    ...resetGetAgentFinanceAudits(),

    ...conductorDailyAuditsReducer(),
    ...conductorDailyTransactionAuditsReducer(),
    ...conductorTripAuditsReducer(),
    ...conductorDurationSummaryReducer(),
    ...conductorDurationTicketSummaryReducer(),
    ...conductorDailySummaryReducer(),

    ...agentDailyAuditsReducer(),
    ...agentDailyTransactionAuditsReducer(),
    ...agentTripAuditsReducer(),
    ...agentDurationSummaryReducer(),
    ...agentDurationTicketSummaryReducer(),
    ...agentDailySummaryReducer(),

    ...otherDurationTicketSummaryReducer(),

    ...tripsPaymentGeneralSummary(),

    ...resetStatesReducer(),
  };

  function createConductorFinanceAuditReducer() {
    return {
      ...builder.addCase(
        extraActions.createConductorFinanceAudit.pending,
        (state, action) => {
          state.createConductorFinanceAudit = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createConductorFinanceAudit.fulfilled,
        (state, action) => {
          state.createConductorFinanceAudit = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createConductorFinanceAudit.rejected,
        (state, action) => {
          state.createConductorFinanceAudit = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetCreateConductorFinanceAudit() {
    return {
      ...builder.addCase(
        extraActions.resetCreateConductorFinanceAudit,
        (state, action) => {
          state.resetCreateConductorFinanceAudit = ResetApiState(true);
          state.createConductorFinanceAudit = ResetApiState(null);
        }
      ),
    };
  }

  function editConductorFinanceAuditReducer() {
    return {
      ...builder.addCase(
        extraActions.editConductorFinanceAudit.pending,
        (state, action) => {
          state.editConductorFinanceAudit = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.editConductorFinanceAudit.fulfilled,
        (state, action) => {
          state.editConductorFinanceAudit = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.editConductorFinanceAudit.rejected,
        (state, action) => {
          state.editConductorFinanceAudit = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetEditConductorFinanceAudit() {
    return {
      ...builder.addCase(
        extraActions.resetEditConductorFinanceAudit,
        (state, action) => {
          state.resetEditConductorFinanceAudit = ResetApiState(true);
          state.editConductorFinanceAudit = ResetApiState(null);
        }
      ),
    };
  }

  function deleteConductorFinanceAuditReducer() {
    return {
      ...builder.addCase(
        extraActions.deleteConductorFinanceAudit.pending,
        (state, action) => {
          state.deleteConductorFinanceAudit = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteConductorFinanceAudit.fulfilled,
        (state, action) => {
          state.deleteConductorFinanceAudit = {
            loading: false,
            payload: true,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteConductorFinanceAudit.rejected,
        (state, action) => {
          state.deleteConductorFinanceAudit = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDeleteConductorFinanceAudit() {
    return {
      ...builder.addCase(
        extraActions.resetDeleteConductorFinanceAudit,
        (state, action) => {
          state.resetDeleteConductorFinanceAudit = ResetApiState(true);
          state.deleteConductorFinanceAudit = ResetApiState(null);
        }
      ),
    };
  }

  function conductorFinanceAuditsReducer() {
    return {
      ...builder.addCase(
        extraActions.getConductorFinanceAudits.pending,
        (state, action) => {
          state.conductorFinanceAudits = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductorFinanceAudits.fulfilled,
        (state, action) => {
          state.conductorFinanceAudits = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductorFinanceAudits.rejected,
        (state, action) => {
          state.conductorFinanceAudits = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }
  function resetGetConductorFinanceAudits() {
    return {
      ...builder.addCase(
        extraActions.resetGetConductorFinanceAudits,
        (state, action) => {
          state.resetConductorFinanceAudits = ResetApiState(true);
          state.conductorFinanceAudits = ResetApiState([]);
        }
      ),
    };
  }

  // -----------------------

  function createAgentFinanceAuditReducer() {
    return {
      ...builder.addCase(
        extraActions.createAgentFinanceAudit.pending,
        (state, action) => {
          state.createAgentFinanceAudit = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createAgentFinanceAudit.fulfilled,
        (state, action) => {
          state.createAgentFinanceAudit = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createAgentFinanceAudit.rejected,
        (state, action) => {
          state.createAgentFinanceAudit = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetCreateAgentFinanceAudit() {
    return {
      ...builder.addCase(
        extraActions.resetCreateAgentFinanceAudit,
        (state, action) => {
          state.resetCreateAgentFinanceAudit = ResetApiState(true);
          state.createAgentFinanceAudit = ResetApiState(null);
        }
      ),
    };
  }

  function agentFinanceAuditsReducer() {
    return {
      ...builder.addCase(
        extraActions.getAgentFinanceAudits.pending,
        (state, action) => {
          state.agentFinanceAudits = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getAgentFinanceAudits.fulfilled,
        (state, action) => {
          state.agentFinanceAudits = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getAgentFinanceAudits.rejected,
        (state, action) => {
          state.agentFinanceAudits = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }
  function resetGetAgentFinanceAudits() {
    return {
      ...builder.addCase(
        extraActions.resetGetAgentFinanceAudits,
        (state, action) => {
          state.resetAgentFinanceAudits = ResetApiState(true);
          state.agentFinanceAudits = ResetApiState([]);
        }
      ),
    };
  }

  // -----------------------

  function conductorDailyAuditsReducer() {
    return {
      ...builder.addCase(
        extraActions.getConductorDailyAudits.pending,
        (state, action) => {
          state.conductorDailyAudits = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductorDailyAudits.fulfilled,
        (state, action) => {
          state.conductorDailyAudits = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductorDailyAudits.rejected,
        (state, action) => {
          state.conductorDailyAudits = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductorDailyAuditsByDateRange.pending,
        (state, action) => {
          state.conductorDailyAudits = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductorDailyAuditsByDateRange.fulfilled,
        (state, action) => {
          state.conductorDailyAudits = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductorDailyAuditsByDateRange.rejected,
        (state, action) => {
          state.conductorDailyAudits = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function conductorDailyTransactionAuditsReducer() {
    return {
      ...builder.addCase(
        extraActions.getConductorDailyTransactionAudits.pending,
        (state, action) => {
          state.conductorDailyAuditTransactions = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductorDailyTransactionAudits.fulfilled,
        (state, action) => {
          state.conductorDailyAuditTransactions = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductorDailyTransactionAudits.rejected,
        (state, action) => {
          state.conductorDailyAuditTransactions = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function conductorTripAuditsReducer() {
    return {
      ...builder.addCase(
        extraActions.getConductorTripAudits.pending,
        (state, action) => {
          state.conductorTripAudits = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductorTripAudits.fulfilled,
        (state, action) => {
          state.conductorTripAudits = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductorTripAudits.rejected,
        (state, action) => {
          state.conductorTripAudits = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function conductorDurationSummaryReducer() {
    return {
      ...builder.addCase(
        extraActions.getConductorDurationSummary.pending,
        (state, action) => {
          state.conductorDurationSummary = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductorDurationSummary.fulfilled,
        (state, action) => {
          state.conductorDurationSummary = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductorDurationSummary.rejected,
        (state, action) => {
          state.conductorDurationSummary = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function conductorDurationTicketSummaryReducer() {
    return {
      ...builder.addCase(
        extraActions.getConductorDurationTicketSummary.pending,
        (state, action) => {
          state.conductorDurationTicketSummary = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductorDurationTicketSummary.fulfilled,
        (state, action) => {
          state.conductorDurationTicketSummary = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductorDurationTicketSummary.rejected,
        (state, action) => {
          state.conductorDurationTicketSummary = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function conductorDailySummaryReducer() {
    return {
      ...builder.addCase(
        extraActions.getConductorDailySummary.pending,
        (state, action) => {
          state.conductorDailySummary = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductorDailySummary.fulfilled,
        (state, action) => {
          state.conductorDailySummary = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductorDailySummary.rejected,
        (state, action) => {
          state.conductorDailySummary = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function agentDailyAuditsReducer() {
    return {
      ...builder.addCase(
        extraActions.getAgentDailyAudits.pending,
        (state, action) => {
          state.agentDailyAudits = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getAgentDailyAudits.fulfilled,
        (state, action) => {
          state.agentDailyAudits = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getAgentDailyAudits.rejected,
        (state, action) => {
          state.agentDailyAudits = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function agentDailyTransactionAuditsReducer() {
    return {
      ...builder.addCase(
        extraActions.getAgentDailyTransactionAudits.pending,
        (state, action) => {
          state.agentDailyAuditTransactions = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getAgentDailyTransactionAudits.fulfilled,
        (state, action) => {
          state.agentDailyAuditTransactions = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getAgentDailyTransactionAudits.rejected,
        (state, action) => {
          state.agentDailyAuditTransactions = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function agentTripAuditsReducer() {
    return {
      ...builder.addCase(
        extraActions.getAgentTripAudits.pending,
        (state, action) => {
          state.agentTripAudits = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getAgentTripAudits.fulfilled,
        (state, action) => {
          state.agentTripAudits = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getAgentTripAudits.rejected,
        (state, action) => {
          state.agentTripAudits = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function agentDurationSummaryReducer() {
    return {
      ...builder.addCase(
        extraActions.getAgentDurationSummary.pending,
        (state, action) => {
          state.agentDurationSummary = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getAgentDurationSummary.fulfilled,
        (state, action) => {
          state.agentDurationSummary = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getAgentDurationSummary.rejected,
        (state, action) => {
          state.agentDurationSummary = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function agentDurationTicketSummaryReducer() {
    return {
      ...builder.addCase(
        extraActions.getAgentDurationTicketSummary.pending,
        (state, action) => {
          state.agentDurationTicketSummary = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getAgentDurationTicketSummary.fulfilled,
        (state, action) => {
          state.agentDurationTicketSummary = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getAgentDurationTicketSummary.rejected,
        (state, action) => {
          state.agentDurationTicketSummary = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function agentDailySummaryReducer() {
    return {
      ...builder.addCase(
        extraActions.getAgentDailySummary.pending,
        (state, action) => {
          state.agentDailySummary = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getAgentDailySummary.fulfilled,
        (state, action) => {
          state.agentDailySummary = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getAgentDailySummary.rejected,
        (state, action) => {
          state.agentDailySummary = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function otherDurationTicketSummaryReducer() {
    return {
      ...builder.addCase(
        extraActions.getOtherDurationTicketSummary.pending,
        (state, action) => {
          state.otherDurationTicketSummary = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getOtherDurationTicketSummary.fulfilled,
        (state, action) => {
          state.otherDurationTicketSummary = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getOtherDurationTicketSummary.rejected,
        (state, action) => {
          state.otherDurationTicketSummary = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function tripsPaymentGeneralSummary() {
    return {
      ...builder.addCase(
        extraActions.getTripsPaymentStatus.pending,
        (state, action) => {
          state.tripsPaymentStatuses = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getTripsPaymentStatus.fulfilled,
        (state, action) => {
          state.tripsPaymentStatuses = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getTripsPaymentStatus.rejected,
        (state, action) => {
          state.tripsPaymentStatuses = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.createConductorFinanceAudit = ResetApiState(null);
        state.resetCreateConductorFinanceAudit = ResetApiState(null);
        state.editConductorFinanceAudit = ResetApiState(null);
        state.resetEditConductorFinanceAudit = ResetApiState(null);
        state.deleteConductorFinanceAudit = ResetApiState(null);
        state.resetDeleteConductorFinanceAudit = ResetApiState(null);
        state.conductorFinanceAudits = ResetApiState([]);
      }),
    };
  }
}

const FinanceAuditSlice = createSlice({
  name,
  initialState: ConductorFinanceAuditInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const FinanceAuditActions = {
  ...FinanceAuditSlice.actions,
  ...extraActions,
};
export const FinanceAuditReducer = FinanceAuditSlice.reducer;
